/**
 * /outreach — Phase 3 outreach dashboard + Follow Queue.
 *
 * One-table view of every realtor with their per-platform contact status.
 * Filters surface the most common outreach workflows:
 *   - "Not contacted on Instagram" → realtor has IG URL but no outreach_log
 *     entry on that channel
 *   - "Met but no online follow-up" → met_in_person_at set but no DM logged
 *   - "Touched, no reply" → at least one outreach but no `reply`/`replied` row
 *
 * Follow Queue mode (Phase 2 — ranking + numbering computed on the SERVER):
 *   The /outreach/dashboard endpoint returns rows already in the deterministic
 *   working order (un-followed first, top performers + most socials on top,
 *   flagged partners floated below today's batch, completed sink to bottom)
 *   with a per-row `queue_rank`. The client paginates that order via infinite
 *   scroll (no more 2,000-row client cap) and only layers on ephemeral UI:
 *   the "hold my place" highlight and today's completion counter.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, MessageCircle, Check, Search, Filter,
  CalendarCheck2, AlertCircle, Handshake, ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ICON_BY_KEY } from "@/components/SocialOutreachButtons";
import { PLATFORMS } from "@/lib/outreach";
import { SortableTH, useResizableColumns } from "@/lib/sortableTable";
import { useAuth } from "@/contexts/AuthContext";
import { RealtorEditDialog } from "@/components/realtors/RealtorForms";
import StatTile from "@/components/outreach/StatTile";
import OutreachTableRow from "@/components/outreach/OutreachTableRow";
import OutreachRow from "@/components/outreach/OutreachRow";
import { queueStats, withTouchStats, DEFAULT_CHANNELS } from "@/components/outreach/utils";
import DailyTargetWheel from "@/components/social/DailyTargetWheel";
import AlphabetRolodex from "@/components/realtors/AlphabetRolodex";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import useServerSearchList from "@/hooks/useServerSearchList";
import BrokeragePeekModal from "@/components/brokerages/BrokeragePeekModal";

const ROLODEX_LETTERS = [
  "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M",
  "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "#",
];

const FILTER_PRESETS = [
  { key: "all", label: "All realtors" },
  { key: "needs_follow_up", label: "Needs follow-up (past referrals)" },
  { key: "not_contacted_on:ig", label: "Not contacted on Instagram" },
  { key: "not_contacted_on:fb", label: "Not contacted on Facebook" },
  { key: "not_contacted_on:li", label: "Not contacted on LinkedIn" },
  { key: "not_contacted_on:tt", label: "Not contacted on TikTok" },
  { key: "not_contacted_on:x", label: "Not contacted on X" },
  { key: "not_contacted_on:yt", label: "Not contacted on YouTube" },
  { key: "not_contacted_on:th", label: "Not contacted on Threads" },
  { key: "not_contacted_on:email", label: "Never emailed" },
  { key: "met_but_no_online", label: "Met in person, no online follow-up" },
  { key: "touched_no_reply", label: "Touched, no reply" },
  { key: "sort_by_stale", label: "Sort by stale-since (oldest first)" },
];

// Discrete threshold buckets — the user asked for a "slider" that only
// stops on these six values. Rendered as a segmented pill group above
// the preset filter. Applies globally: drives both the "needs follow-up"
// preset AND the red badge next to each realtor's name.
const FOLLOW_UP_BUCKETS = [30, 60, 90, 120, 150, 180];
const FOLLOW_UP_STORAGE_KEY = "outreach.followUpThresholdDays";
const QUEUE_MODE_STORAGE_KEY = "outreach.queueMode";
// Daily "batch to work through today" size. User-configurable via a
// ±5 stepper (min 5, max 100), default 20. Persisted per-browser.
const QUEUE_TARGET_STORAGE_KEY = "outreach.queueDailyTarget";
const QUEUE_TARGET_DEFAULT = 20;
const QUEUE_TARGET_STEP = 5;
const QUEUE_TARGET_MIN = 5;
const QUEUE_TARGET_MAX = 100;
// Today's completion counter — resets automatically at midnight local time.
// Shape: { date: "YYYY-MM-DD", count: N }
const QUEUE_DAILY_DONE_KEY = "outreach.queueDailyDone";
// "Hold my place" — clicking any social control on a realtor's row freezes
// its queue position for this long so the live re-sort doesn't slide the
// person you're mid-way through verifying to a new spot. Each further click
// on the SAME realtor restarts the countdown.
const HOLD_MS = 60 * 1000;

function loadInitialThreshold() {
  try {
    const raw = localStorage.getItem(FOLLOW_UP_STORAGE_KEY);
    const n = Number(raw);
    if (FOLLOW_UP_BUCKETS.includes(n)) return n;
  } catch { /* SSR / disabled storage — fall through */ }
  return 90;
}

function todayKey() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function loadInitialQueueTarget() {
  try {
    const raw = localStorage.getItem(QUEUE_TARGET_STORAGE_KEY);
    const n = Number(raw);
    if (Number.isFinite(n) && n >= QUEUE_TARGET_MIN && n <= QUEUE_TARGET_MAX && n % QUEUE_TARGET_STEP === 0) {
      return n;
    }
  } catch { /* fall through */ }
  return QUEUE_TARGET_DEFAULT;
}

function loadInitialDailyDone() {
  try {
    const raw = localStorage.getItem(QUEUE_DAILY_DONE_KEY);
    if (!raw) return { date: todayKey(), count: 0 };
    const parsed = JSON.parse(raw);
    if (parsed && parsed.date === todayKey() && Number.isFinite(Number(parsed.count))) {
      return { date: parsed.date, count: Number(parsed.count) };
    }
  } catch { /* fall through */ }
  return { date: todayKey(), count: 0 };
}

export default function Outreach() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [totals, setTotals] = useState({});
  const [channelPopularity, setChannelPopularity] = useState([]);
  const [channelStats, setChannelStats] = useState(null);
  const [preset, setPreset] = useState("all");
  const [search, setSearch] = useState("");
  // Server-powered search (debounced + cancel-previous).
  const debouncedSearch = useDebouncedValue(search, 300);
  const isSearching = debouncedSearch.trim().length > 0;
  const [selected, setSelected] = useState(new Set());
  const [sort, setSort] = useState("");  // "" = follow API's default order
  // A-Z rolodex jump (browse mode only). {ch, n} so re-clicking re-jumps.
  const [letterJump, setLetterJump] = useState(null);
  // Follow-queue mode — server-ordered deterministic working order.
  const [queueMode, setQueueMode] = useState(() => {
    try { return localStorage.getItem(QUEUE_MODE_STORAGE_KEY) !== "0"; } catch { return true; }
  });
  // Which platforms count toward the queue — persisted on the user profile
  // so it follows them across devices.
  const [channels, setChannels] = useState(() =>
    (Array.isArray(user?.follow_channels) ? user.follow_channels : DEFAULT_CHANNELS),
  );
  // Days-since-last-contact threshold — drives the "Needs follow-up"
  // preset filter AND the red badge next to each realtor's name.
  const [thresholdDays, setThresholdDays] = useState(loadInitialThreshold);
  // Daily batch size — how many realtors the queue asks the user to
  // work through today. User-configurable via a ±5 stepper, default 20.
  const [queueTarget, setQueueTarget] = useState(loadInitialQueueTarget);
  // Debounced copy for the SERVER (re-ranks + flagged band) so dragging the
  // wheel doesn't fire a refetch on every detent. Display cap uses the live
  // value so the "X of N" progress reacts instantly.
  const debouncedQueueTarget = useDebouncedValue(queueTarget, 400);
  // Today's "done" counter — resets automatically at midnight local.
  const [dailyDone, setDailyDone] = useState(loadInitialDailyDone);
  // Per-row last-known completion state — lets the daily counter detect an
  // incomplete→complete transition WITHOUT double-counting rows that arrive
  // already-complete on later infinite-scroll pages.
  const completeStateRef = React.useRef(new Map());
  // "Hold my place" — the last realtor whose socials you clicked is pinned
  // for HOLD_MS. Shape: { id, rowSnapshot, expiresAt }.
  const [hold, setHold] = useState(null);
  const rowsRef = React.useRef([]);
  // In-place realtor profile dialog (same UX as the Realtors tab).
  const [editing, setEditing] = useState(null);
  const [peekBrokerageId, setPeekBrokerageId] = useState(null);
  const [brokerages, setBrokerages] = useState(null); // null = not fetched yet
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    try {
      localStorage.setItem(FOLLOW_UP_STORAGE_KEY, String(thresholdDays));
    } catch { /* storage disabled — non-critical */ }
  }, [thresholdDays]);

  useEffect(() => {
    try {
      localStorage.setItem(QUEUE_MODE_STORAGE_KEY, queueMode ? "1" : "0");
    } catch { /* non-critical */ }
  }, [queueMode]);

  useEffect(() => {
    try {
      localStorage.setItem(QUEUE_TARGET_STORAGE_KEY, String(queueTarget));
    } catch { /* non-critical */ }
  }, [queueTarget]);

  useEffect(() => {
    try {
      localStorage.setItem(QUEUE_DAILY_DONE_KEY, JSON.stringify(dailyDone));
    } catch { /* non-critical */ }
  }, [dailyDone]);

  // Re-check the date once a minute so long-lived sessions roll the counter.
  useEffect(() => {
    const tick = () => {
      const t = todayKey();
      setDailyDone((s) => (s.date === t ? s : { date: t, count: 0 }));
    };
    const iv = setInterval(tick, 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // ----- Unified server data source -------------------------------------
  // ONE infinite-scroll list drives every mode. The params it sends decide
  // the ordering the SERVER applies: search → name match; queue → follow
  // queue order + queue_rank; sort → column sort; else → default. In browse
  // mode (no search, no queue) an A-Z rolodex jump is available.
  const rolodexEligible = !isSearching && !queueMode;
  // Browse default: when social channels are selected, prioritise the list by
  // those channels (matches-first; un-followed → review → followed); otherwise
  // fall back to A-Z rolodex order. An explicit column sort always wins.
  const browseDefaultSort = channels.length ? "channels:priority" : "realtor:asc";
  const effectiveSort = rolodexEligible ? (sort || browseDefaultSort) : sort;
  const showRolodex = rolodexEligible && effectiveSort === "realtor:asc";

  const listFetch = useCallback(async ({ offset, limit, reset, signal }) => {
    const params = { threshold_days: thresholdDays, limit, offset };
    if (preset.startsWith("not_contacted_on:")) params.not_contacted_on = preset.split(":")[1];
    else if (preset === "met_but_no_online") params.met_but_no_online = true;
    else if (preset === "touched_no_reply") params.touched_no_reply = true;
    else if (preset === "sort_by_stale") params.sort_by_stale = true;
    else if (preset === "needs_follow_up") params.needs_follow_up = true;

    if (isSearching) {
      params.search = debouncedSearch;
    } else if (queueMode) {
      params.queue = true;
      params.channels = channels.join(",");
      params.queue_target = debouncedQueueTarget;
    } else {
      params.sort = sort || (channels.length ? "channels:priority" : "realtor:asc");
      params.channels = channels.join(",");
      if (reset && showRolodex && letterJump?.ch) params.letter = letterJump.ch;
    }
    const { data } = await api.get("/outreach/dashboard", { params, signal });
    if (offset === 0) {
      setTotals(data.totals || {});
      if (Array.isArray(data.channel_popularity)) setChannelPopularity(data.channel_popularity);
      if (data.channel_stats) setChannelStats(data.channel_stats);
    }
    return { rows: data.rows || [], total: data.total || 0, offset: data.offset ?? offset };
  }, [thresholdDays, preset, isSearching, debouncedSearch, queueMode, channels, debouncedQueueTarget, sort, showRolodex, letterJump]);

  const depsKey = JSON.stringify({
    td: thresholdDays,
    preset,
    s: isSearching ? debouncedSearch : "",
    queue: !isSearching && queueMode,
    ch: !isSearching ? channels : [],
    qt: (!isSearching && queueMode) ? debouncedQueueTarget : 0,
    sort: (!isSearching && !queueMode) ? effectiveSort : "",
    lj: showRolodex && letterJump ? `${letterJump.ch}:${letterJump.n}` : "",
  });

  const {
    rows,
    setRows,
    total,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    reload,
  } = useServerSearchList({
    fetchPage: listFetch,
    depsKey,
    pageSize: 100,
    enabled: true,
  });

  // Re-baseline the completion tracker whenever the query resets — a channel
  // toggle / preset change re-orders the whole list and must not be read as
  // "work done today".
  useEffect(() => {
    completeStateRef.current = new Map();
  }, [depsKey]);

  // Keep a ref to the latest rows so holdRealtor can snapshot synchronously.
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  // Patch a single row in place (follow / verify / accept) so an in-place
  // update reflects without a refetch → the queue never jumps.
  const mapRow = useCallback((id, transform) => {
    setRows((rs) => (rs || []).map((r) => (r.id === id ? transform(r) : r)));
  }, [setRows]);

  // ----- "Hold my place" -----------------------------------------------
  const holdRealtor = useCallback((id) => {
    if (!id) return;
    setHold((prev) => {
      const expiresAt = Date.now() + HOLD_MS;
      if (prev && prev.id === id) return { ...prev, expiresAt };
      const snap = (rowsRef.current || []).find((r) => r.id === id) || null;
      return { id, rowSnapshot: snap, expiresAt };
    });
  }, []);

  useEffect(() => {
    if (!hold) return undefined;
    const ms = hold.expiresAt - Date.now();
    if (ms <= 0) { setHold(null); return undefined; }
    const t = setTimeout(() => setHold(null), ms);
    return () => clearTimeout(t);
  }, [hold]);

  // When the hold expires (→ null) or switches to a different realtor,
  // sweep completed rows out of the queue so they don't pile up.
  const prevHoldIdRef = React.useRef(null);
  useEffect(() => {
    const prevId = prevHoldIdRef.current;
    const currId = hold?.id || null;
    prevHoldIdRef.current = currId;
    if (prevId && prevId !== currId && queueMode && !isSearching) {
      setRows((rs) => rs.filter((r) => {
        if (r.id === currId) return true;          // keep the newly-held row
        const qs = queueStats(r, channels);
        return !qs.complete;
      }));
    }
  }, [hold, queueMode, isSearching, channels, setRows]);

  const toggleChannel = async (key) => {
    const next = channels.includes(key)
      ? channels.filter((c) => c !== key)
      : [...channels, key];
    setChannels(next);
    try {
      await api.put("/me/profile", { follow_channels: next });
    } catch (e) {
      toast.error("Couldn't save channel selection", { description: formatApiError(e) });
    }
  };

  // Column definitions — widths persist to localStorage under
  // "outreach-list-v2". Sorting is now server-side (header click sends a
  // `sort` param and the list refetches in the new order).
  const outreachCols = useResizableColumns("outreach-list-v2", [
    { key: "realtor",    label: "Realtor",    width: 260, sortable: true },
    { key: "brokerage",  label: "Brokerage",  width: 200, sortable: true },
    { key: "channels",   label: "Socials",    width: 260, sortable: false },
    { key: "last_touch", label: "Last contacted", width: 160, sortable: true },
    { key: "referrals",  label: "Referrals",  width: 100, sortable: true },
    { key: "revenue",    label: "Revenue",    width: 120, sortable: true },
    { key: "touches",    label: "Touches",    width: 90,  sortable: true },
  ]);

  // ----- In-place row patchers ------------------------------------------
  const onFollowChanged = (id, key, ts) => {
    holdRealtor(id);
    mapRow(id, (r) => {
      const follows = { ...(r.follows || {}) };
      if (ts) follows[key] = ts; else delete follows[key];
      return { ...r, follows };
    });
    // Update channel stats header count in real-time
    setChannelStats((prev) => {
      if (!prev?.channels?.[key]) return prev;
      const ch = { ...prev.channels[key] };
      ch.following = Math.max(0, (ch.following || 0) + (ts ? 1 : -1));
      return { ...prev, channels: { ...prev.channels, [key]: ch } };
    });
  };
  const onLinkRemoved = (id, urlField) => {
    holdRealtor(id);
    // Find the platform key for this URL field so we can update the header stat
    const platformKey = PLATFORMS.find((p) => p.urlField === urlField)?.key;
    // Check if this realtor had a follow on this platform before removal
    const row = (rows || []).find((r) => r.id === id);
    const hadFollow = row && platformKey && row.follows?.[platformKey];
    mapRow(id, (r) => ({ ...r, [urlField]: "" }));
    // Decrement the channel total (and following if they'd followed it)
    if (platformKey) {
      setChannelStats((prev) => {
        if (!prev?.channels?.[platformKey]) return prev;
        const ch = { ...prev.channels[platformKey] };
        ch.total = Math.max(0, (ch.total || 0) - 1);
        if (hadFollow) ch.following = Math.max(0, (ch.following || 0) - 1);
        return { ...prev, channels: { ...prev.channels, [platformKey]: ch } };
      });
    }
  };
  const onContactLogged = async (id) => {
    if (!id) return;
    holdRealtor(id);
    try {
      const { data } = await api.get(`/realtors/${id}`);
      mapRow(id, (r) => withTouchStats({ ...r, ...data }));
    } catch (_) { /* silent */ }
  };
  const onSocialVerified = (id, urlField, entry) => {
    holdRealtor(id);
    mapRow(id, (r) => ({
      ...r,
      verified_socials: { ...(r.verified_socials || {}), [urlField]: entry },
    }));
  };
  const onPatch = (updated) => {
    if (!updated || !updated.id) return;
    holdRealtor(updated.id);
    mapRow(updated.id, (r) => withTouchStats({ ...r, ...updated }));
  };

  // ----- In-place profile dialog ----------------------------------------
  const openRealtor = async (id) => {
    try {
      const reqs = [api.get(`/realtors/${id}`)];
      if (brokerages === null) reqs.push(api.get("/brokerages"));
      const [{ data: r }, brokRes] = await Promise.all(reqs);
      if (brokRes) setBrokerages(brokRes.data || []);
      if (isAdmin && locations.length === 0) {
        api.get("/locations")
          .then(({ data }) => setLocations(data.locations || []))
          .catch(() => {});
      }
      setEditing(r);
    } catch (e) {
      toast.error("Couldn't open realtor", { description: formatApiError(e) });
    }
  };

  const displayRows = useMemo(() => rows || [], [rows]);

  // A-Z rolodex (browse mode). All letters clickable; the server resolves
  // the jump offset even for letters not yet loaded.
  const listTopRef = React.useRef(null);
  const rolodexFirstId = useMemo(() => {
    const m = {};
    for (const ch of ROLODEX_LETTERS) m[ch] = ch;
    return m;
  }, []);
  const jumpToLetter = useCallback((_id, ch) => {
    setLetterJump((p) => ({ ch, n: (p?.n || 0) + 1 }));
    requestAnimationFrame(() => {
      listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);
  // Clear a stale letter jump whenever the query context changes.
  useEffect(() => { setLetterJump(null); }, [debouncedSearch, preset, queueMode, thresholdDays]);

  // Today's progress → drives BOTH the banner and the chip cap so they
  // always agree. `remainingToday` = target − done (floored at 0).
  const doneToday = dailyDone.date === todayKey() ? dailyDone.count : 0;
  const remainingToday = Math.max(0, queueTarget - doneToday);

  // Broadcast remaining count to the sidebar badge so it ticks down live.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent("outreach-queue-update", {
        detail: { remaining: remainingToday },
      }));
    } catch (_) { /* older browsers */ }
  }, [remainingToday]);

  // Queue rank chips: countdown from `remainingToday` → 1. The top
  // incomplete row shows "20 left", the next "19 left", etc. Completing a
  // row shrinks remainingToday so the whole column shifts down naturally.
  const queueRankById = useMemo(() => {
    if (isSearching || !queueMode) return {};
    const out = {};
    let shown = 0;
    for (const r of displayRows) {
      const qs = queueStats(r, channels);
      if (qs.complete) continue;
      if (shown >= remainingToday) break;
      shown += 1;
      out[r.id] = remainingToday - shown + 1;   // countdown: 20, 19, … 1
    }
    return out;
  }, [displayRows, isSearching, queueMode, remainingToday, channels]);

  // Detect incomplete→complete transitions on rows we've already seen so the
  // daily counter increments once. Newly-appended (scrolled-in) rows are just
  // baselined, never counted.
  useEffect(() => {
    if (!queueMode || isSearching || !rows) return;
    let newly = 0;
    const map = completeStateRef.current;
    for (const r of rows) {
      const qs = queueStats(r, channels);
      if (!qs.hasWork) { map.set(r.id, true); continue; }
      const prev = map.get(r.id);
      if (prev === undefined) { map.set(r.id, qs.complete); continue; }
      if (qs.complete && !prev) newly += 1;
      map.set(r.id, qs.complete);
    }
    if (newly > 0) {
      setDailyDone((s) => {
        const t = todayKey();
        if (s.date !== t) return { date: t, count: newly };
        return { ...s, count: s.count + newly };
      });
    }
  }, [rows, channels, queueMode, isSearching]);

  const queueSummary = (queueMode && !isSearching)
    ? { doneToday, remaining: remainingToday, target: queueTarget }
    : null;

  // Channel display order: SELECTED channels first (so the operator's active
  // filters cluster on the left), then unselected — each group ordered by how
  // common the channel is in THIS area's data. Applied to the toggle chips AND
  // every realtor's socials column so selected categories always lead.
  const channelDisplayOrder = useMemo(() => {
    const base = channelPopularity.length ? channelPopularity : PLATFORMS.map((p) => p.key);
    const selected = base.filter((k) => channels.includes(k));
    const unselected = base.filter((k) => !channels.includes(k));
    return [...selected, ...unselected];
  }, [channelPopularity, channels]);

  // Ordered platform objects for the queue-channel toggle chips.
  const orderedPlatforms = useMemo(() => {
    const idx = Object.fromEntries(channelDisplayOrder.map((k, i) => [k, i]));
    return [...PLATFORMS].sort((a, b) => (idx[a.key] ?? 99) - (idx[b.key] ?? 99));
  }, [channelDisplayOrder]);

  // Header sort clicks always leave queue mode — clicking "Follow queue"
  // again restores the server's deterministic order.
  const onHeaderSort = (s) => {
    setQueueMode(false);
    setSort(s);
  };

  const toggleAll = () => {
    if (selected.size === displayRows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(displayRows.map((r) => r.id)));
    }
  };

  const toggleOne = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Infinite scroll sentinel — one for every mode.
  const sentinelRef = React.useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore();
        }
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, loadMore]);

  return (
    <div className="space-y-5" data-testid="outreach-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Phase 3 · Social outreach
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900 mt-0.5">
            Outreach
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Every realtor with their per-platform contact status. Tap a platform icon to launch
            the native app — then &quot;Mark as followed&quot; for the green check.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2.5 text-xs"
          onClick={() => navigate("/templates")}
          data-testid="outreach-templates-link"
        >
          <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
          Manage templates
        </Button>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile icon={Users} label="In list" value={totals.count ?? "—"} testid="stat-count" />
        <StatTile icon={CalendarCheck2} label="Met in person" value={totals.met_in_person ?? "—"} testid="stat-met" />
        <StatTile icon={MessageCircle} label="Ever touched" value={totals.ever_touched ?? "—"} testid="stat-touched" />
        <StatTile
          icon={AlertCircle}
          label={`Cold >${thresholdDays}d`}
          value={totals.needs_follow_up ?? "—"}
          testid="stat-needs-follow-up"
          tone={(totals.needs_follow_up || 0) > 0 ? "warn" : "default"}
          hint="Show cold referral partners — highest revenue first"
          onClick={() => {
            setQueueMode(false);
            setSort("revenue:desc");
            setPreset("needs_follow_up");
          }}
        />
      </div>

      {/* Follow-up threshold — segmented pill "slider" (30/60/90/120/150/180d). */}
      <div
        className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
        data-testid="follow-up-threshold-slider"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Label className="text-xs text-slate-500 font-medium inline-flex items-center gap-1.5">
            <Handshake className="h-3.5 w-3.5 text-rose-500" />
            Flag a referral partner if I haven&apos;t contacted them in
          </Label>
          <div className="text-[11px] text-slate-500">
            Applies to red badges + &quot;Needs follow-up&quot; filter
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {FOLLOW_UP_BUCKETS.map((d) => {
            const active = d === thresholdDays;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setThresholdDays(d)}
                className={
                  "px-3 py-1.5 rounded-full text-xs font-medium tabular-nums transition-colors border " +
                  (active
                    ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:border-rose-300 hover:text-rose-700")
                }
                data-testid={`follow-up-threshold-${d}`}
                aria-pressed={active}
              >
                {d}d
              </button>
            );
          })}
        </div>
      </div>

      {/* Follow queue bar — mode toggle + channel selection + progress */}
      <div
        className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
        data-testid="follow-queue-bar"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => { setQueueMode((v) => !v); setSort(""); }}
              className={
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " +
                (queueMode
                  ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")
              }
              data-testid="follow-queue-toggle"
              aria-pressed={queueMode}
              title="Deterministic working order: un-followed first, top performers + most socials on top, completed sink to the bottom"
            >
              <ListChecks className="h-3.5 w-3.5" />
              Follow queue {queueMode ? "· on" : "· off"}
            </button>
            <div className="flex items-center gap-2">
              <DailyTargetWheel
                value={queueTarget}
                onChange={setQueueTarget}
                min={QUEUE_TARGET_MIN}
                max={QUEUE_TARGET_MAX}
                step={QUEUE_TARGET_STEP}
                testid="queue-target-wheel"
              />
              <span
                className="text-[11px] leading-tight text-slate-600 font-medium select-none"
                title="How many realtors the queue should serve up as today's batch"
                data-testid="queue-target-label"
              >
                realtors<br />per day
              </span>
            </div>
            {channelStats ? (
              <>
                <div className="text-xs font-semibold text-slate-700" data-testid="queue-stats-area-total">
                  <span className="text-slate-900">{channelStats.realtors_total}</span> realtors in {channelStats.area_label || "your area"}
                </div>
                <div
                  className="text-[11px] inline-flex items-center gap-1 text-amber-700"
                  data-testid="queue-stats-unverified"
                  title="Realtors with social profiles awaiting your review"
                >
                  <AlertCircle className="h-3 w-3" />
                  {channelStats.unverified} unverified social profile{channelStats.unverified === 1 ? "" : "s"}
                </div>
              </>
            ) : null}
          </div>
          {queueSummary ? (
            <div className="text-[11px] text-slate-500 tabular-nums" data-testid="follow-queue-progress">
              <span className="text-emerald-700 font-semibold">{queueSummary.doneToday} of {queueSummary.target}</span>
              {" done today · "}
              <span className={
                "font-semibold " +
                (queueSummary.remaining === 0 ? "text-emerald-700" : "text-slate-900")
              }>
                {queueSummary.remaining === 0 ? "Batch complete 🎉" : `${queueSummary.remaining} to go`}
              </span>
            </div>
          ) : null}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {orderedPlatforms.map((p) => {
            const Icon = ICON_BY_KEY[p.key];
            const active = channels.includes(p.key);
            const stat = channelStats?.channels?.[p.key];
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => toggleChannel(p.key)}
                className={
                  "inline-flex flex-col items-center justify-center gap-0.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors min-h-[46px] " +
                  (active
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                    : "bg-white text-slate-400 border-slate-200 hover:border-slate-300")
                }
                data-testid={`queue-channel-${p.key}`}
                aria-pressed={active}
                title={
                  stat
                    ? `Following ${stat.following} of ${stat.total} ${p.label.split(" ")[0]} profiles in this area`
                    : (active ? `${p.label} counts toward the queue` : `${p.label} ignored (you don't follow here)`)
                }
              >
                <span className="inline-flex items-center gap-1">
                  {active ? <Check className="h-3 w-3" /> : null}
                  <Icon className="h-3 w-3" />
                  {p.label.split(" ")[0]}
                </span>
                {stat ? (
                  <span className="inline-flex items-center gap-0.5 tabular-nums leading-none" data-testid={`queue-channel-stat-${p.key}`}>
                    <span className={active ? "font-semibold text-slate-900" : "font-semibold text-slate-500"}>{stat.following}</span>
                    <span className="text-slate-400">/{stat.total}</span>
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">
          Checked platforms drive the queue order + completion — untick the ones you don&apos;t
          have accounts on. Ordered by how common each channel is in your data. Saved to your profile.
        </p>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-2 items-end">
          <div>
            <Label htmlFor="filter-preset" className="text-xs text-slate-500 font-medium mb-1 inline-flex items-center gap-1">
              <Filter className="h-3 w-3" /> Preset filter
            </Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger id="filter-preset" data-testid="outreach-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTER_PRESETS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="filter-search" className="text-xs text-slate-500 font-medium mb-1 inline-flex items-center gap-1">
              <Search className="h-3 w-3" /> Search
            </Label>
            <Input
              id="filter-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, brokerage, city, notes..."
              data-testid="outreach-search"
            />
          </div>
        </div>
        {selected.size > 0 ? (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
            <div className="text-xs text-slate-600">
              <strong className="text-slate-900">{selected.size}</strong> selected
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelected(new Set())}
              className="text-xs"
              data-testid="outreach-clear-selection"
            >
              Clear selection
            </Button>
          </div>
        ) : null}
      </div>

      {/* Table */}
      {loading && displayRows.length === 0 ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : displayRows.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center">
          <Users className="h-8 w-8 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-600">
            {isSearching ? "No realtors match your search." : "No realtors match this filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div ref={listTopRef} className="scroll-mt-24" />
          <div className="text-xs text-slate-500" data-testid="outreach-search-count">
            Showing <strong className="text-slate-700 tabular-nums">{displayRows.length}</strong>
            {" of "}
            <strong className="text-slate-700 tabular-nums">{total}</strong>
            {isSearching
              ? ` match${total === 1 ? "" : "es"} for “${debouncedSearch.trim()}”`
              : queueMode ? " in your follow queue" : " realtors"}
          </div>
          <div className="flex items-stretch gap-2">
          {showRolodex ? (
            <div className="shrink-0 self-stretch hidden sm:block" data-testid="outreach-rolodex-wrap">
              <AlphabetRolodex
                letters={ROLODEX_LETTERS}
                firstIdByLetter={rolodexFirstId}
                onJump={jumpToLetter}
              />
            </div>
          ) : null}
          <div className="flex-1 min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
          {/* Desktop table with sortable + resizable columns. */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "44px", minWidth: "44px" }} />
                {outreachCols.colGroup}
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left" style={{ width: "44px" }}>
                    <Checkbox
                      checked={selected.size > 0 && selected.size === displayRows.length}
                      onCheckedChange={toggleAll}
                      data-testid="outreach-select-all"
                      aria-label="Select all"
                    />
                  </th>
                  <SortableTH col={outreachCols.by("realtor")} sort={queueMode ? "" : sort} onSort={onHeaderSort} onBeginResize={outreachCols.beginDrag}>Realtor</SortableTH>
                  <SortableTH col={outreachCols.by("brokerage")} sort={queueMode ? "" : sort} onSort={onHeaderSort} onBeginResize={outreachCols.beginDrag}>Brokerage</SortableTH>
                  <SortableTH col={outreachCols.by("channels")} sort={queueMode ? "" : sort} onSort={onHeaderSort} onBeginResize={outreachCols.beginDrag}>Socials</SortableTH>
                  <SortableTH col={outreachCols.by("last_touch")} sort={queueMode ? "" : sort} onSort={onHeaderSort} onBeginResize={outreachCols.beginDrag}>Last contacted</SortableTH>
                  <SortableTH col={outreachCols.by("referrals")} sort={queueMode ? "" : sort} onSort={onHeaderSort} onBeginResize={outreachCols.beginDrag} align="center">Referrals</SortableTH>
                  <SortableTH col={outreachCols.by("revenue")} sort={queueMode ? "" : sort} onSort={onHeaderSort} onBeginResize={outreachCols.beginDrag} align="right">Revenue</SortableTH>
                  <SortableTH col={outreachCols.by("touches")} sort={queueMode ? "" : sort} onSort={onHeaderSort} onBeginResize={outreachCols.beginDrag} align="center">Touches</SortableTH>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayRows.map((r) => (
                  <OutreachTableRow
                    key={r.id}
                    row={r}
                    currency={user?.currency || "CAD"}
                    platformOrder={channelDisplayOrder}
                    selected={selected.has(r.id)}
                    queueRank={isSearching ? undefined : queueRankById[r.id]}
                    held={!isSearching && hold?.id === r.id}
                    holdExpiresAt={!isSearching && hold?.id === r.id ? hold.expiresAt : null}
                    onSocialInteract={() => holdRealtor(r.id)}
                    onToggle={() => toggleOne(r.id)}
                    onContactLogged={onContactLogged}
                    onFollowChanged={onFollowChanged}
                    onLinkRemoved={onLinkRemoved}
                    onSocialVerified={onSocialVerified}
                    onPatch={onPatch}
                    onBrokerageClick={setPeekBrokerageId}
                    onView={() => openRealtor(r.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile card list */}
          <div className="sm:hidden divide-y divide-slate-100">
            {displayRows.map((r) => (
              <OutreachRow
                key={r.id}
                row={r}
                currency={user?.currency || "CAD"}
                platformOrder={channelDisplayOrder}
                selected={selected.has(r.id)}
                queueRank={isSearching ? undefined : queueRankById[r.id]}
                held={!isSearching && hold?.id === r.id}
                holdExpiresAt={!isSearching && hold?.id === r.id ? hold.expiresAt : null}
                onSocialInteract={() => holdRealtor(r.id)}
                onToggle={() => toggleOne(r.id)}
                onContactLogged={onContactLogged}
                onFollowChanged={onFollowChanged}
                onLinkRemoved={onLinkRemoved}
                onSocialVerified={onSocialVerified}
                onPatch={onPatch}
                onBrokerageClick={setPeekBrokerageId}
                onView={() => openRealtor(r.id)}
              />
            ))}
          </div>
          </div>
          </div>
          {/* Infinite-scroll sentinel + Load more fallback. */}
          <div ref={sentinelRef} aria-hidden="true" className="h-1" />
          {loadingMore ? (
            <div className="text-center text-xs text-slate-400 py-2" data-testid="outreach-loading-more">
              Loading more…
            </div>
          ) : hasMore ? (
            <div className="flex justify-center py-2">
              <button
                type="button"
                onClick={loadMore}
                className="text-xs font-medium text-[hsl(var(--brand-strong))] hover:underline"
                data-testid="outreach-load-more"
              >
                Load more ({total - displayRows.length} more)
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* In-place profile dialog — same component the Realtors tab uses. */}
      <RealtorEditDialog
        realtor={editing}
        brokerages={brokerages || []}
        locations={locations}
        isAdmin={isAdmin}
        onClose={() => setEditing(null)}
        onPatch={(updated) => {
          if (updated && updated.id) {
            mapRow(updated.id, (r) => ({ ...r, ...updated }));
          }
        }}
        onDone={(updated) => {
          setEditing(null);
          if (updated && updated.id && updated._deleted) {
            setRows((rs) => (rs || []).filter((r) => r.id !== updated.id));
          } else if (updated && updated.id) {
            mapRow(updated.id, (r) => ({ ...r, ...updated }));
          } else {
            reload();
          }
        }}
      />

      {/* Brokerage peek — slide-in detail sheet from clicking a brokerage name. */}
      <BrokeragePeekModal
        brokerageId={peekBrokerageId}
        onClose={() => setPeekBrokerageId(null)}
      />
    </div>
  );
}
